"use client";

import Image from "next/image";
import { useRef, useState } from "react";
import { Check } from "lucide-react";
import {
  m,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "motion/react";
import { useInView } from "@/lib/hooks/use-in-view";
import { useTranslation } from "@/lib/hooks/use-translation";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useRevealInView } from "@/lib/hooks/use-reveal-in-view";
import { useScrollerRef } from "@/lib/hooks/use-scroller-ref";
import CrmMock from "@/components/home/mockups/crm-mock";
import DashboardMock from "@/components/home/mockups/dashboard-mock";
import PosMock from "@/components/home/mockups/pos-mock";

type Case = {
  id: string;
  /** Photo for the problem panel. */
  image: string;
  /** Key under `home.problemSolution.cases` holding this card's alt, problem,
      solution and tags. */
  key: string;
  /** Two capability lines under the solution copy, as in the comp. They are
      product names in both languages, so they stay here rather than in the
      locale files. */
  features: [string, string];
  /** The live product mock-up that fills the right half of the panel. It only
      plays its self-demo once the deck says this card is the one in front. */
  device: React.ComponentType<{ active?: boolean }>;
};

const CASES: Case[] = [
  {
    id: "branches",
    image: "/v2/problem-branches.webp",
    key: "branches",
    features: ["Smart Dashboard", "Responsive For All Device"],
    device: DashboardMock,
  },
  {
    id: "campaigns",
    image: "/v2/problem-systems.webp",
    key: "campaigns",
    features: ["CRM Loyalty", "Customer Insights"],
    device: CrmMock,
  },
  {
    id: "ordering",
    image: "/v2/problem-closing.webp",
    key: "orders",
    features: ["POS & QR Ordering", "Kitchen Display System"],
    device: PosMock,
  },
];

/** Scroll distance spent on each hand-off, on top of the pinned viewport.
 *
 * This is the whole feel of the section: it is how far the reader has to push
 * the wheel to move one card. A larger number is not a slower animation in the
 * usual sense — nothing here runs on a clock — it is more scroll spread across
 * the same movement, so each hand-off resolves gradually instead of snapping
 * past. Tuned by eye through 85 → 55 → 130: at 55vh a normal wheel flick threw
 * a card off screen in one gesture, which reads as abrupt rather than quick.
 * The cost is a longer pin, and it is worth paying here. */
const SCROLL_PER_CARD = "130vh";

/**
 * Smoothing on the raw scroll position, so the cards follow the wheel instead
 * of being welded to it. A wheel or a trackpad delivers scroll in discrete
 * jumps, and a transform mapped straight off it steps with them; running it
 * through a spring turns those steps into one continuous move.
 *
 * Soft and overdamped. The softness is the point — the cards glide to where
 * the scroll has asked them to be rather than arriving with it, and that
 * trailing fraction of a second is most of what reads as smooth. Overdamped
 * so there is no overshoot: the front card's whole reason for resting at
 * exactly scale(1) is that a fraction of a percent off would blur its type
 * (see the keyframe note in ScrollCard), and a spring that rings past its
 * target would sit there wobbling around it.
 */
const FOLLOW = { stiffness: 80, damping: 20, mass: 0.45, restDelta: 0.0005 };

/** Resting depth of the cards waiting behind the front one. */
const DEPTH = [
  { scale: 1, y: 0, rotate: 0 },
  { scale: 0.94, y: 30, rotate: -1.1 },
  { scale: 0.88, y: 58, rotate: 1.5 },
];

/** Share of a hand-off the outgoing card spends sliding off screen. */
const EXIT_SHARE = 0.6;

/**
 * How early the incoming card finishes rising, as a share of a hand-off. It
 * settles onto scale(1) here and holds, so the card is never read at a
 * fractional scale — see the keyframe comment in ScrollCard.
 */
const ARRIVE_SHARE = 0.5;

/** The stack only pins where a whole card fits in the viewport. */
const PIN_QUERY = "(min-width: 1024px) and (min-height: 780px)";

/**
 * ProblemSolution — v2 section 3: three problem/solution cards dealt off a
 * stack as the page scrolls.
 *
 * The section is a tall track with a pinned pane inside it. `useScroll` turns
 * the track's travel into 0 → 1, and each card maps its own slice of that: it
 * waits behind the stack (scaled down, nudged lower, slightly tilted), rises
 * to the front, then slides off to alternating sides on the way out — the
 * Motion card-stack deck, dealt by the scrollbar rather than by a drag.
 *
 * Because the order is fixed, `z-index` never has to change: card 1 is always
 * above card 2, and simply moving out of the way is what reveals the next one.
 *
 * Each card's right half is a live mock-up of the product (see
 * components/home/mockups) rather than a screenshot, so the claim on the left
 * is something the reader can go and try. Only the front card is interactive —
 * the rest are `inert`, which keeps the buttons of an invisible card out of
 * the tab order and out of reach of the pointer.
 *
 * Narrow or short viewports (and `prefers-reduced-motion`) get the same three
 * cards as a plain vertical list — a card is far taller than a phone screen
 * once its two panels stack, so pinning it there would trap the reader in a
 * pane taller than their screen. That list is also what renders on the server
 * and before hydration.
 */
export default function ProblemSolution() {
  const { t } = useTranslation();
  // Repeating rather than one-shot: the deck is the section people scroll
  // back up to, and its entrance should meet them again when they do.
  const sectionRef = useRevealInView<HTMLElement>(0.15);
  const trackRef = useRef<HTMLDivElement>(null);

  // The page scrolls inside #scroll-container (see app/(site)/layout.tsx), not
  // the window, so useScroll has to be pointed at it — and at the scrollport
  // that is in the document *now*, which a language switch replaces. See
  // lib/hooks/use-scroller-ref.ts.
  const scrollerRef = useScrollerRef();

  const { scrollYProgress } = useScroll({
    container: scrollerRef,
    target: trackRef,
    offset: ["start start", "end end"],
  });
  // What the cards and the dots both read — see FOLLOW.
  const progress = useSpring(scrollYProgress, FOLLOW);

  const reduceMotion = useReducedMotion();
  const canPin = useMediaQuery(PIN_QUERY);
  const pinned = canPin && !reduceMotion;

  const [active, setActive] = useState(0);
  useMotionValueEvent(progress, "change", (p) => {
    setActive(Math.round(p * (CASES.length - 1)));
  });

  /** Jump the page to the scroll offset where `index` is the front card. */
  function goTo(index: number) {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;
    const travel = track.offsetHeight - scroller.clientHeight;
    scroller.scrollTo({
      top: track.offsetTop + (travel * index) / (CASES.length - 1),
      behavior: "smooth",
    });
  }

  return (
    <section
      ref={sectionRef}
      aria-label={t("home.problemSolution.aria")}
      className="ps-reveal bg-[#FCFCFC]"
    >
      <div
        ref={trackRef}
        className="relative"
        style={
          pinned
            ? {
                height: `calc(100dvh + ${CASES.length - 1} * ${SCROLL_PER_CARD})`,
              }
            : undefined
        }
      >
        {/* top-13 matches the scroll container's pt-13: the fixed navbar sits
            over the first 3.25rem of the scrollport, so that's where a pinned
            pane has to stop. */}
        <div
          className={
            pinned
              ? "sticky top-13 flex h-[calc(100dvh-3.25rem)] flex-col items-center justify-center px-4 md:px-[80px]"
              : "flex flex-col items-center px-4 py-12 md:px-[80px]"
          }
        >
          <h2 className="ps-title max-w-[20ch] text-center text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
            {t("home.problemSolution.heading")}
          </h2>

          {/* Pinned: every card lands in the same grid cell, so the wrapper
              takes its height from the tallest one and the deck needs no
              hard-coded height. Unpinned: a plain column. */}
          {/* `ps-stack` only on the pinned deck. Unpinned, the three cards
              run to ~3081px on a phone, so raising the wrapper would fade all
              three in together the moment the section's observer fires — long
              before the reader has scrolled to the second one. `ps-list`
              carries no entrance of its own; each card brings its own (see
              `CaseCard`). */}
          <div
            className={
              pinned
                ? "ps-stack mt-6 grid w-full max-w-[1200px] items-start"
                : "ps-list mt-8 flex w-full max-w-[1200px] flex-col gap-6"
            }
          >
            {CASES.map((data, i) =>
              pinned ? (
                <ScrollCard
                  key={data.id}
                  data={data}
                  index={i}
                  total={CASES.length}
                  progress={progress}
                  live={i === active}
                />
              ) : (
                <CaseCard key={data.id} data={data} />
              ),
            )}
          </div>

          {pinned && (
            <div className="ps-controls mt-14 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2.5">
                {CASES.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-current={active === i}
                    aria-label={t("home.problemSolution.cardAria", {
                      problem: t(`home.problemSolution.cases.${c.key}.problem`),
                    })}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      active === i
                        ? "w-7 bg-[#0084EB]"
                        : "w-2.5 bg-black/15 hover:bg-black/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * One card wired to its slice of the track.
 *
 * Its keyframes are laid out in card units — the card is at depth 2 when it is
 * two hand-offs away, at depth 0 when it is front, and gone one hand-off
 * later — then converted to progress and trimmed to the part that actually
 * falls inside 0 → 1. The trim matters: motion compiles scroll-linked values
 * into native scroll timelines, and a keyframe outside [0, 1] is a hard error
 * there rather than something it clamps away.
 */
function ScrollCard({
  data,
  index,
  total,
  progress,
  live,
}: {
  data: Case;
  index: number;
  total: number;
  progress: MotionValue<number>;
  /** Front card — the only one whose mock-up accepts focus or clicks. */
  live: boolean;
}) {
  const step = 1 / (total - 1);
  const front = index * step;
  // Alternate the exit side so the deck doesn't sweep one way the whole time.
  const away = index % 2 === 0 ? -1 : 1;

  // Progress → depth, where depth -1 is "already dealt away", 0 is front, and
  // 1 and 2 are the two waiting places behind it.
  //
  // Depth 0 is listed twice on purpose, which is what keeps the front card
  // sharp. Between two identical keyframes nothing interpolates, so the card
  // finishes its rise half a hand-off early and then sits at exactly scale(1)
  // for the whole time it is the card being read. A composited layer is
  // rasterised once for a given scale, so a card resting on a fractional
  // scale — 0.97, say — is a 0.97-sized bitmap stretched back up, and every
  // line of type in it goes soft.
  const stops = [
    { p: front - 2 * step, depth: 2 },
    { p: front - step, depth: 1 },
    { p: front - step * ARRIVE_SHARE, depth: 0 },
    { p: front, depth: 0 },
    // The exit is compressed into the first 60% of the hand-off. The card
    // leaves at full opacity — dealt off the deck, not dissolved — so it has
    // to be gone before the next card is fully forward.
    { p: front + step * EXIT_SHARE, depth: -1 },
  ].filter((s) => s.p >= 0 && s.p <= 1);

  const input = stops.map((s) => s.p);
  const frame = (pick: (d: (typeof DEPTH)[number]) => number, exit: number) =>
    stops.map((s) => (s.depth < 0 ? exit : pick(DEPTH[s.depth])));

  const scale = useTransform(
    progress,
    input,
    frame((d) => d.scale, 1),
  );
  const y = useTransform(
    progress,
    input,
    frame((d) => d.y, 0),
  );
  const rotate = useTransform(
    progress,
    input,
    frame((d) => d.rotate, away * 9),
  );
  // Travel in vw, not in a share of the card's own width: the card is capped
  // at 1200px but the viewport is not, so a percentage that clears the screen
  // at 1440 leaves an edge poking in at 1920.
  const x = useTransform(
    progress,
    input,
    stops.map((s) => (s.depth < 0 ? `${away * 110}vw` : "0vw")),
  );

  return (
    <m.div
      inert={!live}
      className="col-start-1 row-start-1 will-change-transform"
      style={{ zIndex: total - index, x, y, scale, rotate }}
    >
      <CaseCard data={data} live={live} dealt />
    </m.div>
  );
}

/** One problem/solution pair — the comp's Frame 354. */
type CaseCopy = {
  imageAlt: string;
  problem: string;
  solution: string;
  tags: string[];
};

function CaseCard({
  data,
  live,
  dealt,
}: {
  data: Case;
  live?: boolean;
  /** True inside the pinned deck, where the stack — not the card — owns both
      the entrance and whether the card is on screen at all. */
  dealt?: boolean;
}) {
  const { t } = useTranslation();
  const copy = t(`home.problemSolution.cases.${data.key}`, {
    returnObjects: true,
  }) as unknown as CaseCopy;
  const Device = data.device;

  // This is the branch that renders on a phone (and on any viewport too short
  // to pin), where the three cards make a ~3081px column. Each one waits for
  // its own arrival rather than riding a single wrapper entrance, so the
  // second and third still have their rise left to play when the reader gets
  // to them.
  //
  // `useInView` rather than a class-adding hook: the flag has to go through
  // React's own className, because a hook that adds the class imperatively
  // loses it the next time React rewrites the attribute. It keeps observing,
  // so a card that is scrolled back to rises again.
  const { ref, inView } = useInView<HTMLElement>(0.15);

  // In the deck a card leaves the viewport because it has been dealt away, not
  // because the reader scrolled past it, so its own entrance would fire again
  // every time the deck rewound — on top of the slide back in. There the
  // stack's `ps-rise` is the entrance, and the card is simply visible.
  const revealed = dealt || inView;

  return (
    <article
      ref={ref}
      className={`ps-card overflow-hidden rounded-[28px] border border-[#E7E7E7] bg-[linear-gradient(180deg,#F7F7F7_0%,#FFFFFF_100%)] p-4 shadow-[0_18px_44px_-16px_rgba(15,23,42,0.16)] sm:p-6 lg:p-8 ${
        revealed ? "is-visible" : ""
      }`}
    >
      <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row">
        {/* Problem panel */}
        <div className="ps-glass flex flex-col gap-4 px-6 py-6 sm:px-8 lg:min-h-[379px] lg:w-[363px] lg:shrink-0">
          <div className="relative min-h-[168px] flex-1 overflow-hidden rounded-[21px] bg-[#EDF4FD]">
            {/* The slot is the viewport minus a *fixed* gutter, not a
                fraction of it, so a vw estimate is wrong at both ends: the
                90vw this used to claim asked a 375px phone for 337 CSS px of
                image where it renders 249, which at DPR 2 reached past the
                640w candidate to the 750w one for no visible gain. The three
                steps are the section's own padding jumps — 63px a side, +16
                at `sm` (p-4→p-6, px-6→px-8), then the container's own gutter
                from `md`. Past `lg` the panel is a fixed 363px column. */}
            <Image
              src={data.image}
              alt={copy.imageAlt}
              fill
              sizes="(max-width: 639px) calc(100vw - 126px), (max-width: 767px) calc(100vw - 158px), (max-width: 1023px) calc(100vw - 286px), 300px"
              className="object-cover"
            />
          </div>
          {/* Pulled up so the pill straddles the photo's bottom edge — it
              reads as a label stuck onto the picture rather than a caption
              floating under it. The negative margin has to cover the parent's
              own gap-4 on top of the overlap it buys. */}
          <div className="relative -mt-11 flex flex-col items-start gap-4">
            <Pill className="shadow-[0_0.4em_1em_-0.4em_rgba(0,132,235,0.7)]">
              Problem?
            </Pill>
            <p className="text-[18px]/[26px] font-medium text-[#666666] sm:text-[20px]/[28px]">
              {copy.problem}
            </p>
          </div>
        </div>

        {/* Solution panel: copy on the left, the working mock-up on the right.
            The comp lets the device bleed off the panel edge; it's fully in
            frame here instead, because it's something to use rather than a
            picture of a product. */}
        <div className="ps-glass flex-1 px-6 py-6 sm:px-8 lg:min-h-[379px]">
          <div className="flex h-full flex-col gap-6 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 flex-col gap-4 lg:self-stretch">
              <div className="flex flex-col items-start gap-3">
                <Pill>Solution</Pill>
                <p className="text-[18px]/[26px] font-medium text-[#666666] sm:text-[20px]/[28px]">
                  {copy.solution}
                </p>
              </div>

              <ul className="flex flex-col gap-3">
                {data.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-4">
                    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[linear-gradient(152deg,rgba(255,171,26,0.58)_0%,rgba(255,255,255,0)_100%),radial-gradient(ellipse_73%_73%_at_50%_50%,#FFD666_0%,#FFFFFF_100%)]">
                      <Check
                        aria-hidden
                        strokeWidth={3}
                        className="size-[18px] text-white"
                      />
                    </span>
                    <span className="text-[17px]/[24px] text-[#666666] sm:text-[18px]/[24px]">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* mt-auto pins the tags to the panel's bottom edge like the
                  comp. */}
              <ul className="mt-auto flex flex-wrap gap-2">
                {copy.tags.map((tag) => (
                  <li
                    key={tag}
                    className="rounded-2xl bg-[rgba(194,194,194,0.09)] px-3 py-1 text-[15px]/[24px] font-medium text-black/60 shadow-[0_4px_7px_-2px_rgba(0,0,0,0.1)] outline outline-1 -outline-offset-1 outline-white/30 backdrop-blur-[16px] sm:text-[16px]/[24px]"
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mx-auto w-full max-w-[400px] shrink-0 pb-[6%] lg:mx-0 lg:w-[46%] lg:max-w-none">
              <Device active={live} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function Pill({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-2xl bg-[#0084EB] px-4 py-2 text-[20px]/[28px] font-semibold text-white sm:text-[24px]/[32px] ${className}`}
    >
      {children}
    </span>
  );
}
